import styled from '@emotion/styled';
import { Trans } from '@lingui/react/macro';
import { Skeleton, Tooltip } from '@mui/material';

import { VideaTypography } from '../../common-components/elements/VideaTypography/VideaTypography';
import {
  useGetIncompleteTreatments,
  useGetPatientFindings,
} from '../../react-query/useInsightsRecap';
import {
  blueUltramarine070,
  blueUltramarine300,
} from '../../theme/videa-style-tokens';

import {
  Card,
  Flex,
  SubCard,
  TipsAndUpdatesOutlinedIcon,
} from './CommonStyles';

const IncompleteTreatmentRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const FlexContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 2em;
  .flex {
    display: flex;
    align-items: center;
    gap: 0.5em;
  }
`;

export const IncompleteTreatmentCountsCard = () => {
  const { data, isLoading } = useGetIncompleteTreatments();
  const { data: patientFindingsData } = useGetPatientFindings();

  if (isLoading) {
    return (
      <Card>
        <Skeleton variant="rectangular" height="2em" data-testid="skeleton" />
        <Flex className="center space-between">
          <Skeleton
            variant="rectangular"
            height="1em"
            width="8em"
            data-testid="skeleton"
          />
          <Skeleton
            variant="circular"
            height="2em"
            width="2em"
            data-testid="skeleton"
          />
        </Flex>
        <Flex className="center space-between">
          <Skeleton
            variant="rectangular"
            height="1em"
            width="8em"
            data-testid="skeleton"
          />
          <Skeleton
            variant="circular"
            height="2em"
            width="2em"
            data-testid="skeleton"
          />
        </Flex>
        <Flex className="center space-between">
          <Skeleton
            variant="rectangular"
            height="1em"
            width="8em"
            data-testid="skeleton"
          />
          <Skeleton
            variant="circular"
            height="2em"
            width="2em"
            data-testid="skeleton"
          />
        </Flex>
        <Skeleton variant="rectangular" height="5em" data-testid="skeleton" />
      </Card>
    );
  }

  const recapPatientsWithAnalysis =
    patientFindingsData?.recapPeriod.patientAnalyses.patientsWithAnalysis;
  const isTreatmentDataCountIssue = data?.treatmentsPlannedNotCompleted.some(
    (treatmentCount) => {
      const totalTreatments = treatmentCount.totalTreatments;
      return (
        totalTreatments > 999 &&
        !!recapPatientsWithAnalysis &&
        totalTreatments / recapPatientsWithAnalysis > 15
      );
    },
  );

  if (isTreatmentDataCountIssue) {
    return null;
  }

  return (
    <Card>
      <VideaTypography color="white" variant="labelSmallStrong">
        <Trans>
          {`${data?.distinctPatients} ${
            data?.distinctPatients && data.distinctPatients === 1
              ? 'patient has'
              : 'patients have'
          }`}{' '}
          incomplete treatments{' '}
          <Tooltip title="Data includes treatments planned up to 1 year ago">
            <VideaTypography variant="bodysmall" color={blueUltramarine070}>
              planned
            </VideaTypography>
          </Tooltip>{' '}
          &gt; 90 days ago
        </Trans>
      </VideaTypography>
      {data?.treatmentsPlannedNotCompleted.map((treatmentCount) => {
        const { treatmentType } = treatmentCount;

        return (
          <IncompleteTreatmentRow key={treatmentType}>
            <VideaTypography
              color="white"
              variant="bodyExtrasmall"
              textTransform="capitalize"
            >
              <Trans>
                {treatmentType !== 'SRP' ? treatmentType.toLowerCase() : 'SRP'}{' '}
                Treatments
              </Trans>
            </VideaTypography>
            <div className="vai-inline-flex vai-items-center vai-justify-center vai-min-w-7 vai-h-7 vai-px-2 vai-rounded-full vai-bg-white vai-text-blue-600 vai-text-sm vai-font-medium">
              {treatmentCount.totalTreatments}
            </div>
          </IncompleteTreatmentRow>
        );
      })}
      <SubCard>
        <FlexContainer>
          <VideaTypography variant="labelSmallStrong" className="flex">
            <Trans>
              <TipsAndUpdatesOutlinedIcon />
              Next steps
            </Trans>
          </VideaTypography>
          <div>
            <VideaTypography variant="bodyExtrasmall">
              <Trans>Your current case </Trans>
            </VideaTypography>
            <Tooltip
              title="Note: Case acceptance is calculated based on completed treatments
              only (not scheduled)."
            >
              <VideaTypography
                variant="bodyExtrasmall"
                color={blueUltramarine300}
              >
                <Trans>acceptance rate</Trans>
              </VideaTypography>
            </Tooltip>
            <VideaTypography variant="bodyExtrasmall">
              <Trans>
                {' '}
                is {data && (data.caseAcceptanceRate * 100).toFixed(0)}%, you
                can increase case acceptance by presenting more AI findings to
                patients.{' '}
                <a
                  href="https://help.videa.ai/how-to-talk-to-your-patients-about-ai"
                  target="_blank"
                  rel="noreferrer"
                >
                  Click to learn how to talk to patients about AI.
                </a>
              </Trans>
            </VideaTypography>
            <br />
          </div>
        </FlexContainer>
      </SubCard>
    </Card>
  );
};
